import React, { useState, useEffect } from 'react';
import { FiEdit, FiTrash2, FiPlus } from 'react-icons/fi';

import Table from '../../../components/admin/common/Table';
import Button from '../../../components/admin/common/Button';
import Modal from '../../../components/admin/common/Modal';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../../services/categoryService';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent: ''
  });

  // Fetch categories
  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await getCategories();
      setCategories(response.data || []);
      setError(null);
    } catch (err) {
      setError(err.toString());
      console.error('Error fetching categories:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Open modal for creating a new category
  const handleAddClick = () => {
    setSelectedCategory(null);
    setFormData({ name: '', description: '', parent: '' });
    setModalOpen(true);
  };

  // Open modal for editing existing category
  const handleEditClick = (category, e) => {
    if (e) e.stopPropagation();
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      parent: category.parent?._id || ''
    });
    setModalOpen(true);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (category, e) => {
    if (e) e.stopPropagation();
    setSelectedCategory(category);
    setDeleteModalOpen(true);
  };

  // Save category (create or update)
  const handleSaveCategory = async () => {
    try {
      if (selectedCategory) {
        // Update existing category
        await updateCategory(selectedCategory._id, formData);
      } else {
        // Create new category
        await createCategory(formData);
      }
      
      // Refresh categories list
      fetchCategories();
      setModalOpen(false);
    } catch (err) {
      setError(err.toString());
      console.error('Error saving category:', err);
    }
  };

  // Delete category
  const handleDeleteCategory = async () => {
    try {
      await deleteCategory(selectedCategory._id);
      fetchCategories();
      setDeleteModalOpen(false);
    } catch (err) {
      setError(err.toString());
      console.error('Error deleting category:', err);
    }
  };

  // Table columns definition
  const columns = [
    {
      key: 'name',
      header: 'Name',
      width: '30%'
    },
    {
      key: 'parent',
      header: 'Parent Category',
      render: (category) => category.parent?.name || '-'
    },
    {
      key: 'description',
      header: 'Description',
      render: (category) => category.description || '-'
    },
    {
      key: 'products',
      header: 'Products',
      render: (category) => category.products || '0'
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '100px',
      render: (category) => (
        <div className="flex space-x-2">
          <button 
            onClick={(e) => handleEditClick(category, e)}
            className="text-blue-600 hover:text-blue-800"
            title="Edit"
          >
            <FiEdit size={18} />
          </button>
          <button 
            onClick={(e) => handleDeleteClick(category, e)}
            className="text-red-600 hover:text-red-800"
            title="Delete"
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Categories</h1>
        <Button 
          variant="primary" 
          onClick={handleAddClick}
          icon={<FiPlus />}
        >
          Add Category
        </Button>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
          Error: {error}
        </div>
      )}

      <Table 
        columns={columns} 
        data={categories} 
        isLoading={isLoading}
        emptyMessage="No categories found. Add your first category!"
      />

      {/* Add/Edit Category Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedCategory ? 'Edit Category' : 'Add Category'}
        size="md"
        footer={
          <>
            <Button 
              variant="outline" 
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveCategory}
            >
              {selectedCategory ? 'Save Changes' : 'Create Category'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name*
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parent Category
            </label>
            <select
              name="parent"
              value={formData.parent}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="">None (Top Level)</option>
              {categories
                .filter(cat => (!selectedCategory || cat._id !== selectedCategory._id))
                .map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded h-24"
            ></textarea>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Category"
        size="sm"
        footer={
          <>
            <Button 
              variant="outline" 
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteCategory}
            >
              Delete
            </Button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{selectedCategory?.name}</strong>?
        </p>
        {selectedCategory?.products > 0 && (
          <p className="mt-2 text-red-600">
            Warning: This category contains {selectedCategory.products} products.
            Those products will need to be reassigned before deletion.
          </p>
        )}
      </Modal>
    </div>
  );
};

export default CategoriesPage; 